import { computed, onBeforeUnmount, ref } from 'vue';

// Parte da janela que precisa continuar visível para ser arrastada de volta.
const VISIBLE_WIDTH = 96;
const VISIBLE_HEIGHT = 56;
const KEYBOARD_STEP = 24;
const INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, label, [role="button"], [contenteditable="true"]';

const resolveElement = target => target?.$el ?? target ?? null;

/**
 * Janela flutuante arrastável pelo cabeçalho. Move por `left/top` (sem
 * `transform`), para não mudar o bloco de contenção dos popups fixos que
 * abrem dentro dela (DateField, ComboBox).
 */
export function useDraggablePanel(panelRef) {
  const position = ref(null);
  let drag = null;

  const clamp = ({ left, top }, rect) => ({
    left: Math.min(
      Math.max(left, VISIBLE_WIDTH - rect.width),
      window.innerWidth - VISIBLE_WIDTH
    ),
    top: Math.min(Math.max(top, 0), window.innerHeight - VISIBLE_HEIGHT),
  });

  const currentRect = () =>
    resolveElement(panelRef.value)?.getBoundingClientRect() || null;

  const onPointerMove = event => {
    if (!drag) return;
    position.value = clamp(
      {
        left: drag.left + event.clientX - drag.x,
        top: drag.top + event.clientY - drag.y,
      },
      drag.rect
    );
  };

  const stopDrag = () => {
    drag = null;
    document.body.style.userSelect = '';
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', stopDrag);
    window.removeEventListener('pointercancel', stopDrag);
  };

  const startDrag = event => {
    if (event.button !== 0 || event.target.closest?.(INTERACTIVE_SELECTOR))
      return;
    const rect = currentRect();
    if (!rect) return;
    event.preventDefault();
    drag = {
      x: event.clientX,
      y: event.clientY,
      left: rect.left,
      top: rect.top,
      rect,
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);
  };

  const moveBy = (dx, dy) => {
    const rect = currentRect();
    if (!rect) return;
    position.value = clamp({ left: rect.left + dx, top: rect.top + dy }, rect);
  };

  const KEY_DELTAS = {
    ArrowLeft: [-KEYBOARD_STEP, 0],
    ArrowRight: [KEYBOARD_STEP, 0],
    ArrowUp: [0, -KEYBOARD_STEP],
    ArrowDown: [0, KEYBOARD_STEP],
  };

  const onHandleKeydown = event => {
    const delta = KEY_DELTAS[event.key];
    if (!delta) return;
    event.preventDefault();
    moveBy(...delta);
  };

  const onResize = () => {
    const rect = currentRect();
    if (position.value && rect) position.value = clamp(position.value, rect);
  };
  window.addEventListener('resize', onResize);

  onBeforeUnmount(() => {
    stopDrag();
    window.removeEventListener('resize', onResize);
  });

  const reset = () => {
    stopDrag();
    position.value = null;
  };

  const panelStyle = computed(() =>
    position.value
      ? {
          position: 'fixed',
          margin: '0',
          right: 'auto',
          bottom: 'auto',
          left: `${position.value.left}px`,
          top: `${position.value.top}px`,
        }
      : {}
  );

  return {
    isMoved: computed(() => Boolean(position.value)),
    panelStyle,
    startDrag,
    onHandleKeydown,
    reset,
  };
}
